// This file is responsible for fetching routes and snapping
//  the pins to the right place on the route.
//
// Ambassador: Yatharth.
//
import { produce } from 'immer';
import _, { flatten } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { snappedLatLngsFromRoute, useFetchRoute } from './fetchRoutes';
import { LatLngStr, PinInfo, Route } from './stopTypes';

// When drawing routes, Google Maps "snaps" our pins to the road. We need to get
//  these "snapped" coordinates and draw our location pins there. Otherwise, the
//  pins won’t be "on" the route.
//
// To do this, once `useDrawRoutes()` fetches directions for a route, it returns
//  the coordinates for each stop snapped to the route. We can then draw markers
//  at the snapped coordinates.
//
// We need the useDrawRoutes() hooks to rerun every time our props change.
//  But it’s hard to keep the state updated and consistent, because each
//  call to the Google Maps Routes API resolves on its own. We have
//  different bits of the data coming in at different times, and we don't
//  wait for all of them to finish.
//
// To offer our users a smooth experience, we have a clever way to
//  structure our state. We have two state variables: `snappedLatLngs`
//  (to draw the pins) and `results` (to draw the paths).
//
// `snappedCoordinates` is a Record of Records. The first key is the
//  group index, and the second key is the stop index. The snapped
//  coordinates for each stop is updated as it comes in from the directions
//  API. We check `snappedCoordinates` for as many groups as we expect there
//  to be, smoothly transitioning from the old data to the new data as it comes in.
//
// This way, the whole map does not need to flicker. Instead, individual
//  markers disappear and appear as data comes in. Cool!
//
// If you're changing this, you should be careful and check with Yatharth.
//  There are lots of edge cases.
//
export const useFetchAndSnapRoutes = ({
    map,
    maps,
    routes,
    drawRoutes,
}: {
    map: google.maps.Map | null;
    maps: typeof google.maps | null;
    routes: Route[];
    drawRoutes: boolean;
}): [PinInfo[], (google.maps.routes.Route | null)[]] => {
    const fetchRoute = useFetchRoute({ maps });

    // The snapped coordinates for each stop is updated as it comes in from the
    //  Routes API. A null entry means the API gave us nothing to snap to, in which
    //  case the pin falls back to the stop's original coordinates.
    const [snappedLatLngs, setSnappedLatLngs] = useState<(LatLngStr | null)[][]>([]);
    const setSnappedLatLng = useCallback(
        (groupIndex: number, stopIndex: number, snappedLatLng: LatLngStr | null) => {
            setSnappedLatLngs((prevLatLangs) =>
                produce(prevLatLangs, (draft) => {
                    if (!(groupIndex in draft)) {
                        draft[groupIndex] = [];
                    }
                    draft[groupIndex][stopIndex] = snappedLatLng;
                }),
            );
        },
        [],
    );

    const [results, setResults] = useState<(google.maps.routes.Route | null)[]>([]);
    // We don't use immer here: `google.maps.routes.Route` is a class instance,
    //  which immer's draft types choke on. A plain copy-and-set works fine.
    const setResult = useCallback((groupIndex: number, result: google.maps.routes.Route) => {
        setResults((prevResults) => {
            const nextResults = [...prevResults];
            nextResults[groupIndex] = result;
            return nextResults;
        });
    }, []);

    // Effect to update `snappedLatLngs` and `results` asynchronously
    //  whenever `routes` changes.
    useEffect(() => {
        //

        // If `map` or `maps` is null, then Google Maps is not ready yet.
        //  No worries. This hook will get called again with the new `map`
        //  and `maps` parameters when they get set, and this effect will rerun.
        if (!map || !maps) {
            return;
        }

        // If we're not drawing routes, then no need to fetch anything.
        if (!drawRoutes) {
            return;
        }

        // Clear old results and snapped coordinates.
        setResults([]);
        setSnappedLatLngs([]);

        _.forEach(routes, (route, groupIndex) => {
            //

            // Anytime Axle Ops loads the visit page with 100s of visits across
            //  the entire continental United States, it consumes a lot of
            //  Routes API requests.
            // We should do a thing, where we only draw routes if at least one
            //  stop is visible on the map + if we are zoomed in enough.
            // For now, this is not a problem, because we do not fetch or draw
            //  routes if more than one day is selected. And with our current visit volume
            //  of only ~50–100 visits a day, we end up having only 10–50 direction requests,
            //  so it's fine. Caching the results helps.

            // If it’s just zero or one stops, don’t bother with fetching any directions.
            //  We can just draw the pins in their original locations; no snapping needed.
            if (route.length <= 1) {
                return;
            }

            fetchRoute(route, (result) => {
                snappedLatLngsFromRoute(result).forEach((snappedLatLng, stopIndex) => {
                    setSnappedLatLng(groupIndex, stopIndex, snappedLatLng);
                });
                setResult(groupIndex, result);
            });

            //
        });
    }, [map, maps, routes, drawRoutes, fetchRoute, setResult, setSnappedLatLng]);

    // Latest pin infos to draw, based on the snapped coordinates as they come in.
    const pinInfos: PinInfo[] = useMemo(
        () =>
            flatten(
                routes.map((route, groupIndex) =>
                    route.map((stop, stopIndex) => ({
                        stop: {
                            // If a snapped coordinate it available, it is used;
                            //  otherwise, the stop's original coordinates are used.
                            ...stop,
                            ...snappedLatLngs[groupIndex]?.[stopIndex],
                        },
                        groupIndex,
                        stopIndex,
                    })),
                ),
            ),
        [routes, snappedLatLngs],
    );

    // Latest direction results to draw, based on the direction results as they come in.
    const resultsToDraw = useMemo(
        () => routes.map((route, groupIndex) => results[groupIndex] ?? null),
        [routes, results],
    );

    return [pinInfos, resultsToDraw];
};
